import { CheckIcon, ChevronLeftIcon, SendIcon } from "lucide-react";
import {
	type KeyboardEvent,
	type ReactNode,
	useCallback,
	useRef,
	useState,
} from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type PagedInteractionOption = {
	value: string;
	label: string;
	description?: string;
	requiresText?: boolean;
	textPlaceholder?: string;
};

export type PagedInteractionQuestion = {
	id: string;
	question: string;
	kind: "short_text" | "long_text" | "choice";
	options?: PagedInteractionOption[];
	allowFreeText?: boolean;
	recommendedValue?: string;
	multiSelect?: boolean;
	optional?: boolean;
	placeholder?: string;
	noteMode?: "answer" | "supporting";
	context?: ReactNode;
};

export type PagedInteractionAnswer = {
	value: string | string[];
	typed: boolean;
	note?: string;
};

export type PagedInteractionAnswerMap = Record<
	string,
	PagedInteractionAnswer | undefined
>;

export function isPagedAnswerPresent(
	question: PagedInteractionQuestion,
	answer: PagedInteractionAnswer | undefined,
): boolean {
	if (question.optional && !answer) return true;
	if (!answer) return false;
	if (Array.isArray(answer.value)) {
		if (answer.value.length === 0) return false;
		const requiresText = question.options?.some(
			(option) => option.requiresText && answer.value.includes(option.value),
		);
		return !requiresText || Boolean(answer.note?.trim());
	}
	if (!answer.value.trim()) return false;
	const selectedOption = question.options?.find(
		(option) => option.value === answer.value,
	);
	return !selectedOption?.requiresText || Boolean(answer.note?.trim());
}

function normalizeRecommendation(value: string): string {
	return value.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

function recommendedOptionValue(
	options: readonly PagedInteractionOption[] | undefined,
	recommendedValue: string | undefined,
): string | undefined {
	if (!options || !recommendedValue) return undefined;
	const recommended = normalizeRecommendation(recommendedValue);
	if (!recommended) return undefined;
	const matches = options.filter((option) => {
		const value = normalizeRecommendation(option.value);
		const label = normalizeRecommendation(option.label);
		return [value, label].some(
			(candidate) =>
				candidate.length > 0 &&
				(candidate.includes(recommended) || recommended.includes(candidate)),
		);
	});
	return matches.length === 1 ? matches[0].value : undefined;
}

export function PagedInteractionDock({
	header,
	questions,
	onSubmit,
	isComplete = isPagedAnswerPresent,
	onNavigate,
	disabled,
}: {
	header: string;
	questions: PagedInteractionQuestion[];
	onSubmit: (answers: PagedInteractionAnswerMap) => Promise<void> | void;
	isComplete?: (
		question: PagedInteractionQuestion,
		answer: PagedInteractionAnswer | undefined,
	) => boolean;
	onNavigate?: (questionIndex: number) => void;
	disabled?: boolean;
}) {
	const autoAdvanceTimeoutRef = useRef<
		ReturnType<typeof setTimeout> | undefined
	>(undefined);
	const formRef = useCallback((node: HTMLFormElement | null) => {
		if (!node) return;
		return () => {
			if (autoAdvanceTimeoutRef.current) {
				clearTimeout(autoAdvanceTimeoutRef.current);
				autoAdvanceTimeoutRef.current = undefined;
			}
		};
	}, []);
	const [questionIndex, setQuestionIndex] = useState(0);
	const [answers, setAnswers] = useState<PagedInteractionAnswerMap>({});
	const [submitted, setSubmitted] = useState(false);
	const question =
		questions[Math.min(questionIndex, Math.max(questions.length - 1, 0))];
	const isLast = questionIndex >= questions.length - 1;
	const current = question ? answers[question.id] : undefined;
	const supporting = question?.noteMode === "supporting";
	const selectedTextOption =
		question && current && !current.typed
			? question.options?.find(
					(option) =>
						option.requiresText &&
						(Array.isArray(current.value)
							? current.value.includes(option.value)
							: option.value === current.value),
				)
			: undefined;
	const showTextInput =
		question?.kind !== "choice" ||
		Boolean(selectedTextOption) ||
		question?.allowFreeText === true;
	const typedText = supporting
		? (current?.note ?? "")
		: current?.typed
			? String(current.value)
			: "";
	const suggestedOptionValue = recommendedOptionValue(
		question?.options,
		question?.recommendedValue,
	);

	const setChoice = useCallback(
		(item: PagedInteractionQuestion, value: string) => {
			setAnswers((previous) => {
				if (!item.multiSelect) {
					const requiresText = item.options?.some(
						(option) => option.value === value && option.requiresText,
					);
					return {
						...previous,
						[item.id]: {
							value,
							typed: false,
							note: requiresText ? previous[item.id]?.note : undefined,
						},
					};
				}
				const prior = previous[item.id];
				const selected =
					prior && !prior.typed && Array.isArray(prior.value)
						? prior.value
						: [];
				const next = selected.includes(value)
					? selected.filter((option) => option !== value)
					: [...selected, value];
				const requiresText = item.options?.some(
					(option) => option.requiresText && next.includes(option.value),
				);
				return {
					...previous,
					[item.id]: {
						value: next,
						typed: false,
						note: requiresText ? prior?.note : undefined,
					},
				};
			});
		},
		[],
	);

	const setText = useCallback(
		(questionId: string, value: string, noteMode: boolean) => {
			setAnswers((previous) => {
				const prior = previous[questionId];
				return {
					...previous,
					[questionId]: noteMode
						? {
								value: prior?.value ?? "",
								typed: prior?.typed ?? false,
								note: value,
							}
						: { value, typed: true },
				};
			});
		},
		[],
	);

	const requiredComplete = questions.every((item) =>
		isComplete(item, answers[item.id]),
	);
	const currentComplete = question ? isComplete(question, current) : false;

	const navigate = useCallback(
		(index: number) => {
			const bounded = Math.max(0, Math.min(index, questions.length - 1));
			setQuestionIndex(bounded);
			onNavigate?.(bounded);
		},
		[onNavigate, questions.length],
	);

	const submitAnswers = useCallback(
		async (nextAnswers: PagedInteractionAnswerMap) => {
			if (submitted) return;
			const incompleteIndex = questions.findIndex(
				(item) => !isComplete(item, nextAnswers[item.id]),
			);
			if (incompleteIndex >= 0) {
				navigate(incompleteIndex);
				return;
			}
			setSubmitted(true);
			try {
				await onSubmit(nextAnswers);
			} catch {
				setSubmitted(false);
			}
		},
		[isComplete, navigate, onSubmit, questions, submitted],
	);
	const submit = useCallback(
		() => submitAnswers(answers),
		[answers, submitAnswers],
	);

	const advanceOrSubmit = useCallback(() => {
		if (!currentComplete) return;
		if (!isLast) {
			navigate(questionIndex + 1);
			return;
		}
		void submit();
	}, [currentComplete, isLast, navigate, questionIndex, submit]);

	const skip = useCallback(() => {
		if (!question?.optional) return;
		const next = { ...answers };
		delete next[question.id];
		setAnswers(next);
		if (!isLast) {
			navigate(questionIndex + 1);
			return;
		}
		void submitAnswers(next);
	}, [answers, isLast, navigate, question, questionIndex, submitAnswers]);

	const pick = useCallback(
		(value: string) => {
			if (!question) return;
			if (question.multiSelect) {
				setChoice(question, value);
				return;
			}
			const requiresText = question.options?.some(
				(option) => option.value === value && option.requiresText,
			);
			setChoice(question, value);
			const nextAnswer = {
				value,
				typed: false,
				note: requiresText ? answers[question.id]?.note : undefined,
			};
			if (!isLast && isComplete(question, nextAnswer)) {
				if (autoAdvanceTimeoutRef.current) {
					clearTimeout(autoAdvanceTimeoutRef.current);
				}
				autoAdvanceTimeoutRef.current = setTimeout(() => {
					autoAdvanceTimeoutRef.current = undefined;
					navigate(questionIndex + 1);
				}, 150);
			}
		},
		[answers, isComplete, isLast, question, questionIndex, navigate, setChoice],
	);

	const onKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
		if (event.metaKey || event.ctrlKey || event.altKey || !question) return;
		if (
			event.target instanceof HTMLTextAreaElement ||
			event.target instanceof HTMLInputElement
		) {
			return;
		}
		if (event.key === "Enter") {
			event.preventDefault();
			advanceOrSubmit();
			return;
		}
		if (question.kind !== "choice") return;
		const digit = Number.parseInt(event.key, 10);
		if (Number.isNaN(digit) || digit < 1 || digit > 9) return;
		const option = question.options?.[digit - 1];
		if (!option) return;
		event.preventDefault();
		pick(option.value);
	};

	const selected = (value: string): boolean => {
		if (!question || !current || current.typed) return false;
		return question.multiSelect
			? Array.isArray(current.value) && current.value.includes(value)
			: current.value === value;
	};

	const questionId = question?.id;
	const questionFocusRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (!node || !questionId) return;
			node.closest("form")?.focus();
		},
		[questionId],
	);

	if (!question) return null;

	return (
		<form
			ref={formRef}
			aria-label={header}
			tabIndex={-1}
			className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm"
			onKeyDown={onKeyDown}
			onSubmit={(event) => {
				event.preventDefault();
				advanceOrSubmit();
			}}
		>
			<div
				ref={questionFocusRef}
				className="flex flex-col gap-3 border-b bg-muted/40 px-4 py-3.5"
			>
				<div className="flex items-center gap-2">
					<span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						{header}
					</span>
					{questions.length > 1 ? (
						<Badge variant="secondary">
							{questionIndex + 1} of {questions.length}
						</Badge>
					) : null}
				</div>
				<div className="text-sm font-medium text-foreground">
					{question.question}
				</div>
				{question.context}
				{question.multiSelect ? (
					<p className="text-xs text-muted-foreground">Select one or more.</p>
				) : null}
				{question.kind === "choice" ? (
					<div className="flex flex-col gap-2">
						{question.options?.map((option, index) => {
							const active = selected(option.value);
							return (
								<Button
									key={option.value}
									type="button"
									variant={active ? "default" : "outline"}
									disabled={submitted}
									aria-pressed={active}
									onClick={() => pick(option.value)}
									className="h-auto justify-start gap-3 px-3 py-2.5 text-left"
								>
									<span className="flex min-w-0 flex-1 flex-col gap-0.5">
										<span className="truncate">{option.label}</span>
										{option.description ? (
											<span
												className={cn(
													"whitespace-normal text-xs",
													active
														? "text-primary-foreground/80"
														: "text-muted-foreground",
												)}
											>
												{option.description}
											</span>
										) : null}
									</span>
									{option.value === suggestedOptionValue ? (
										<Badge variant="secondary">Suggested</Badge>
									) : null}
									{active ? (
										<CheckIcon data-icon="inline-end" />
									) : index < 9 ? (
										<Kbd>{index + 1}</Kbd>
									) : null}
								</Button>
							);
						})}
					</div>
				) : null}
				{question.kind === "choice" ? (
					<p className="text-xs text-muted-foreground">
						<Kbd>1–9</Kbd> select · <Kbd>Enter</Kbd> next
						{showTextInput ? (
							<>
								{" "}
								· <Kbd>Shift+Enter</Kbd> newline
							</>
						) : null}
					</p>
				) : null}
			</div>
			<div className="flex items-end gap-2 px-4 py-3">
				{showTextInput ? (
					<Textarea
						id={`paged-interaction-${question.id}`}
						name={`paged-interaction-${question.id}`}
						value={typedText}
						onChange={(event) =>
							setText(question.id, event.target.value, supporting)
						}
						onKeyDown={(event) => {
							if (event.key === "Enter" && !event.shiftKey) {
								event.preventDefault();
								advanceOrSubmit();
							}
						}}
						placeholder={
							selectedTextOption?.textPlaceholder ??
							question.placeholder ??
							"Type your answer"
						}
						disabled={submitted}
						className="max-h-40 min-h-10 flex-1 resize-none py-2 text-sm field-sizing-content"
					/>
				) : null}
				{question.optional ? (
					<div className="flex flex-col items-start gap-0.5">
						<Button
							type="button"
							variant="secondary"
							size="sm"
							disabled={submitted}
							onClick={skip}
						>
							Skip
						</Button>
						<span className="text-xs text-muted-foreground">
							Fact stays unresolved
						</span>
					</div>
				) : null}
				{questionIndex > 0 ? (
					<Button
						type="button"
						variant="ghost"
						size="sm"
						disabled={submitted}
						onClick={() => navigate(questionIndex - 1)}
						className={cn(!showTextInput && "ml-auto")}
					>
						<ChevronLeftIcon data-icon="inline-start" />
						Prev
					</Button>
				) : null}
				<Button
					type="submit"
					size="sm"
					disabled={
						disabled ||
						submitted ||
						(isLast ? !requiredComplete : !currentComplete)
					}
					className={cn(!showTextInput && questionIndex === 0 && "ml-auto")}
				>
					{isLast ? (
						<>
							<SendIcon data-icon="inline-start" />
							{questions.length > 1 ? "Send answers" : "Send"}
						</>
					) : (
						"Next →"
					)}
				</Button>
			</div>
		</form>
	);
}
